import React, {useEffect, useContext, useState, useRef} from "react";
import { Flex, Space, Badge, Avatar, Skeleton, Input, App } from "antd";
import _ from "lodash";
import { FaRegUser, FaTimes, FaArrowAltCircleUp } from "react-icons/fa";
import { API } from "../../../resources/constants"
import language from "../../../resources/languages/en_US"

// Interfaces
import { Ask as AskType, Message as MessageType } from "../../../interfaces/pages/opportunities"
import { AxiosError } from "../../../interfaces/axios";

// Contexts
import GlobalContext from '../../../contexts/global'
import AxiosContext from "../../../contexts/axios";

const { TENDER_CHAT_CREATE, TENDER_CHAT_UPDATE, DOWNLOAD } = API
const { Pages: { Opportunities: { Tender } }, Share, Validation, Message: MessageLanguage, Enum: EnumLanguage } = language
const baseURL = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_API_URL : process.env.REACT_APP_PRODUCTION_API_URL

export default function Ask(props: { source: string, tenderId: string, askId: string|null, messages: MessageType[], loadingAsk: boolean, loadingMessages: boolean, askHistorySearchReset: boolean, setAskId: (askId: string|null) => void, setLoadingAsk: (loadingAsk: boolean) => void, setShowAskBox: (showAskBox: boolean) => void, getAsks: (tenderId: string) => Promise<AskType[]>, getMessages: (askId: string) => Promise<MessageType[]>, setAskHistorySearchReset: (askHistorySearchReset: boolean) => void }) {
    const { source, tenderId, askId, messages, loadingMessages, loadingAsk, askHistorySearchReset, setAskId, setLoadingAsk, setShowAskBox, getAsks, getMessages, setAskHistorySearchReset } = props;

    const { axios } = useContext(AxiosContext)
    const messagesRef  = useRef<HTMLDivElement>(null)
    const { user, accessToken } = useContext(GlobalContext)
    const [prompt, setPrompt] = useState<string>("")
    const { message, modal } = App.useApp()

    useEffect(() => {
        if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }, [loadingAsk])

    useEffect(() => {
        setPrompt("")
    }, [askId])

    const submitAsk = () => {
        if (tenderId && prompt && !loadingMessages && !loadingAsk) {
            setLoadingAsk(true);
            if (askId) axios.patch(TENDER_CHAT_UPDATE + askId, { prompt }, { params: { source, loading: false } }).then(async () => {
                await getMessages(askId);
                await getAsks(tenderId);
                setPrompt("")
                setLoadingAsk(false);
                setAskHistorySearchReset(!askHistorySearchReset);
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                setLoadingAsk(false);
                // @ts-ignore
                message.error(MessageLanguage[data.message]||data.message).then(response => response )
            }); else axios.post(TENDER_CHAT_CREATE, { tender: tenderId, prompt }, { params: { source, loading: false } }).then(async (response: { data: AskType }) => {
                const { data: ask } = response;
                setAskId(ask.id);
                await getMessages(ask.id);
                await getAsks(tenderId);
                setPrompt("")
                setLoadingAsk(false);
                setAskHistorySearchReset(!askHistorySearchReset);
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                setLoadingAsk(false);
                // @ts-ignore
                message.error(MessageLanguage[data.message]||data.message).then(response => response )
            });
        }
    }

    return (
        <Space direction={'vertical'} size={10} style={{width: '100%'}}>
            <div className={'title'}>
                <Flex justify={'space-between'}>
                    <span>{Tender.askAlpha}</span>
                    {
                        (loadingMessages || loadingAsk) ? <FaTimes size={18} style={{cursor:'default'}} /> : <FaTimes size={18} style={{cursor:'pointer'}} onClick={() => {
                            setShowAskBox(false);
                            setAskId(null);
                        }}/>
                    }
                </Flex>
            </div>
            <Space ref={messagesRef} direction={'vertical'} className={'messages'} size={20} style={{width: '100%'}}>
                {
                    (loadingMessages || loadingAsk) ? (
                        <>
                            <Skeleton avatar round active />
                            <Skeleton avatar round active />
                        </>
                    ) : _.isEmpty(messages) ? (
                        <>
                            <Badge count={<Avatar size={25} shape={'square'} icon={<img src={require('../../../resources/media/logo-blue.png')} width={25} />} />} offset={[-20, 4]}>
                                <div className={'message content'}>
                                    {Tender.askAlphaDefaultMessage}
                                </div>
                            </Badge>
                        </>
                    ) : messages.map(({prompt, content}) => (
                        <>
                            <Badge count={<Avatar size={25} shape={'square'} icon={user?.avatar ?
                                <img src={baseURL + DOWNLOAD + user.avatar + `?width=25&height=25&token=${accessToken}`}
                                     width={25}/> : <FaRegUser/>}/>} offset={[-20, 4]}>
                                <div className={'message prompt'}>
                                    {prompt}
                                </div>
                            </Badge>
                            <Badge count={<Avatar size={25} shape={'square'}
                                                  icon={<img src={require('../../../resources/media/logo-blue.png')}
                                                             width={25}/>}/>} offset={[-20, 4]}>
                                <div className={'message content'}>
                                    {content}
                                </div>
                            </Badge>
                        </>
                    ))
                }
            </Space>
            <div className={'ask-input'}>
                <Input
                    size="large"
                    value={prompt}
                    onChange={({ target: { value }}) => setPrompt(value)}
                    disabled={loadingMessages || loadingAsk}
                    placeholder={Tender.askPlaceholder}
                    onKeyPress={({ charCode }) => {
                        if (charCode === 13) submitAsk()
                    }}
                    suffix={
                    <FaArrowAltCircleUp
                        size={24}
                        style={{cursor: (loadingMessages || loadingAsk) ? 'default' : 'pointer'}}
                        onClick={submitAsk}
                    />
                }
                />
            </div>
        </Space>
    )
}